import { useState, useEffect } from "react";
import { 
  Shield, LogIn, LogOut, Phone, Mail, Calendar, Clock,
  Trash2, Users, MessageSquare, AlertTriangle, CheckCircle,
  Loader2
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState("");
  const [loginData, setLoginData] = useState({ username: "", password: "" });
  const [loginLoading, setLoginLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState({ quotes: [], contacts: [] });
  const [activeTab, setActiveTab] = useState("quotes");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const savedToken = localStorage.getItem("fichet_admin_token");
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchData();
    }
  }, [isAuthenticated, token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, leadsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/leads`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok && leadsRes.ok) {
        const statsData = await statsRes.json();
        const leadsData = await leadsRes.json();
        setStats(statsData);
        setLeads(leadsData);
      } else {
        handleLogout();
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginData)
      });

      if (response.ok) {
        const data = await response.json();
        setToken(data.access_token);
        localStorage.setItem("fichet_admin_token", data.access_token);
        setIsAuthenticated(true);
        toast.success("Connexion réussie !");
      } else {
        toast.error("Identifiants incorrects");
      }
    } catch (error) {
      toast.error("Erreur de connexion");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setToken("");
    setIsAuthenticated(false);
    localStorage.removeItem("fichet_admin_token");
    toast.success("Déconnexion réussie");
  };

  const handleDeleteLead = async (type, id) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce lead ?")) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/leads/${type}/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Lead supprimé");
        fetchData();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur de connexion");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#1a3a5c] to-[#0f2744] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#0f2744]">Administration</h1>
            <p className="text-gray-500">Fichet Dépannage</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Identifiant
              </label>
              <input
                type="text"
                value={loginData.username}
                onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                placeholder="admin"
                required
                className="w-full"
                data-testid="admin-username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mot de passe
              </label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                placeholder="••••••••"
                required
                className="w-full"
                data-testid="admin-password"
              />
            </div>
            <button
              type="submit"
              disabled={loginLoading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="admin-login-btn"
            >
              {loginLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Se connecter
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#1a3a5c] to-[#0f2744] rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-[#0f2744]">Fichet Dépannage</h1>
              <p className="text-xs text-gray-500">Tableau de bord</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="w-5 h-5" />
            Déconnexion
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-sm text-gray-500">Total leads</span>
              </div>
              <p className="text-3xl font-bold text-[#0f2744]">{stats.total_leads}</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-amber-600" />
                </div>
                <span className="text-sm text-gray-500">Demandes devis</span>
              </div>
              <p className="text-3xl font-bold text-[#0f2744]">{stats.total_quotes}</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-green-600" />
                </div>
                <span className="text-sm text-gray-500">Messages contact</span>
              </div>
              <p className="text-3xl font-bold text-[#0f2744]">{stats.total_contacts}</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-purple-600" />
                </div>
                <span className="text-sm text-gray-500">Aujourd'hui</span>
              </div>
              <p className="text-3xl font-bold text-[#0f2744]">
                {stats.today_quotes + stats.today_contacts}
              </p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab("quotes")}
              className={`flex-1 px-6 py-4 font-medium transition-colors ${
                activeTab === "quotes"
                  ? "bg-[#0f2744] text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              data-testid="tab-quotes"
            >
              Demandes de devis ({leads.quotes.length})
            </button>
            <button
              onClick={() => setActiveTab("contacts")}
              className={`flex-1 px-6 py-4 font-medium transition-colors ${
                activeTab === "contacts"
                  ? "bg-[#0f2744] text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              data-testid="tab-contacts"
            >
              Messages contact ({leads.contacts.length})
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : activeTab === "quotes" ? (
              <QuotesList 
                quotes={leads.quotes} 
                onDelete={(id) => handleDeleteLead("quote", id)}
                formatDate={formatDate}
              />
            ) : (
              <ContactsList 
                contacts={leads.contacts}
                onDelete={(id) => handleDeleteLead("contact", id)}
                formatDate={formatDate}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function QuotesList({ quotes, onDelete, formatDate }) {
  if (quotes.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Aucune demande de devis pour le moment</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {quotes.map((quote) => (
        <div 
          key={quote.id}
          className={`border rounded-xl p-4 ${
            quote.urgency === "urgent" ? "border-red-300 bg-red-50" : "border-gray-200"
          }`}
          data-testid={`quote-${quote.id}`}
        >
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center gap-3">
              <h3 className="font-semibold text-[#0f2744]">{quote.name}</h3>
              {quote.urgency === "urgent" && (
                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Urgent
                </span>
              )}
            </div>
            <button
              onClick={() => onDelete(quote.id)}
              className="text-gray-400 hover:text-red-600 transition-colors"
              data-testid={`delete-quote-${quote.id}`}
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-2 text-sm">
            <a 
              href={`tel:${quote.phone}`}
              className="flex items-center gap-2 text-blue-600 hover:underline"
            >
              <Phone className="w-4 h-4" />
              {quote.phone}
            </a>
            {quote.city && (
              <span className="flex items-center gap-2 text-gray-600">
                <span className="w-4 h-4 text-center">📍</span>
                {quote.city}
              </span>
            )}
            {quote.service && (
              <span className="flex items-center gap-2 text-gray-600">
                <span className="w-4 h-4 text-center">🔧</span>
                {quote.service}
              </span>
            )}
            <span className="flex items-center gap-2 text-gray-400">
              <Clock className="w-4 h-4" />
              {formatDate(quote.created_at)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ContactsList({ contacts, onDelete, formatDate }) {
  if (contacts.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Aucun message contact pour le moment</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {contacts.map((contact) => (
        <div 
          key={contact.id}
          className="border border-gray-200 rounded-xl p-4"
          data-testid={`contact-${contact.id}`}
        >
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-semibold text-[#0f2744]">{contact.name}</h3>
            <button
              onClick={() => onDelete(contact.id)}
              className="text-gray-400 hover:text-red-600 transition-colors"
              data-testid={`delete-contact-${contact.id}`}
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex flex-wrap gap-4 text-sm mb-3">
            <a 
              href={`tel:${contact.phone}`}
              className="flex items-center gap-2 text-blue-600 hover:underline"
            >
              <Phone className="w-4 h-4" />
              {contact.phone}
            </a>
            {contact.email && (
              <a 
                href={`mailto:${contact.email}`}
                className="flex items-center gap-2 text-blue-600 hover:underline"
              >
                <Mail className="w-4 h-4" />
                {contact.email}
              </a>
            )}
            <span className="flex items-center gap-2 text-gray-400">
              <Clock className="w-4 h-4" />
              {formatDate(contact.created_at)}
            </span>
          </div>

          <p className="text-gray-600 bg-gray-50 p-3 rounded-lg text-sm">
            {contact.message}
          </p>
        </div>
      ))}
    </div>
  );
}
